import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneField } from '@/components/booking/phone-field';
import { countryFieldOf, resolveCountry, guessCountryIso, splitPhone, countryByIso } from '@/lib/countries';

// Step "Basics" — built-in name/email/phone/company/website only.
// Custom fields from the Form Builder are rendered separately by
// QuestionStep so each one can carry its own motivating headline.
// Phone verification is deliberately NOT here any more. It now lives at the
// submit button: prompting under the field let someone walk past it, answer
// every question, and hit a wall at "Book my call" with no way forward.
export function ContactForm({ formData, onChange, formConfig, onFieldBlur }) {
  const setField = (name, value) => onChange({ ...formData, [name]: value });
  // Built-in optional contact fields (default ON when the flag is absent).
  const collectCompany = formConfig?.collect_company !== false;
  const collectWebsite = formConfig?.collect_website !== false;

  // The country is never stored separately: the phone value is E.164, so the
  // code IS the country. That keeps one source of truth and means nothing extra
  // rides along to the API.
  const countryFieldId = countryFieldOf(formConfig);
  const answeredCountry = countryFieldId ? formData.custom_data?.[countryFieldId] : null;
  const fallbackIso = resolveCountry(answeredCountry)?.iso
    || guessCountryIso(formConfig?.default_country || formConfig?.workspace_country);
  const phoneIso = splitPhone(formData.phone, fallbackIso).iso;

  // Picking a code on the phone answers the country question too, when the form
  // asks one and the visitor has not got to it yet.
  const setPhone = (e164, iso) => {
    const next = { ...formData, phone: e164 };
    if (countryFieldId && !answeredCountry) {
      const c = countryByIso(iso);
      if (c) next.custom_data = { ...formData.custom_data, [countryFieldId]: c.name };
    }
    onChange(next);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="first_name">First name *</Label>
          <Input
            id="first_name"
            value={formData.first_name || ''}
            onChange={(e) => setField('first_name', e.target.value)}
            placeholder="John"
            required
            autoComplete="given-name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="last_name">Last name</Label>
          <Input
            id="last_name"
            value={formData.last_name || ''}
            onChange={(e) => setField('last_name', e.target.value)}
            placeholder="Doe"
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => setField('email', e.target.value)}
          onBlur={onFieldBlur}
          placeholder="you@company.com"
          required
          autoComplete="email"
        />
      </div>

      {/* Its own full-width row. Sharing a two-column grid with Company left the
          number box barely wider than the country chip, which is the one field
          where cramped input causes real typos. */}
      <div className="space-y-1.5">
        <Label htmlFor="phone">Phone *</Label>
        <PhoneField
          id="phone"
          value={formData.phone}
          iso={phoneIso}
          onChange={setPhone}
          onBlur={onFieldBlur}
          required
        />
      </div>

      <div className={`grid grid-cols-1 gap-3 ${collectCompany && collectWebsite ? 'sm:grid-cols-2' : ''}`}>
        {collectCompany && (
          <div className="space-y-1.5">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              value={formData.company || ''}
              onChange={(e) => setField('company', e.target.value)}
              placeholder="Your company"
              autoComplete="organization"
            />
          </div>
        )}
        {collectWebsite && (
        <div className="space-y-1.5">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            type="text"
            inputMode="url"
            value={formData.website || ''}
            onChange={(e) => setField('website', e.target.value)}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && !/^https?:\/\//i.test(v) && /\.[a-z]{2,}/i.test(v)) {
                setField('website', `https://${v}`);
              }
            }}
            placeholder="example.com or https://example.com"
            autoComplete="url"
          />
        </div>
        )}
      </div>
    </div>
  );
}
